import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { Scale, Check, Sparkles, FileStack, Layers, CheckCircle2, Gavel } from "lucide-react";
import { NC_COUNTIES } from "@/lib/ncCounties";
import { useI18n } from "@/context/I18nContext";
import { attorneyText, areaLabel } from "@/lib/attorneyI18n";

const PERK_ICONS = [FileStack, Sparkles, Layers];

export default function ForAttorneys() {
  const { lang } = useI18n();
  const L = attorneyText(lang);
  const [form, setForm] = useState({ name: "", email: "", firm: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [areas, setAreas] = useState([]);
  const [listing, setListing] = useState({ name: "", email: "", firm: "", city: "", county: "", phone: "", website: "", bio: "", practice_areas: [] });
  const [listingDone, setListingDone] = useState(false);
  const [listingBusy, setListingBusy] = useState(false);
  const setL = (k, v) => setListing((f) => ({ ...f, [k]: v }));
  const toggleArea = (a) => setListing((f) => ({ ...f, practice_areas: f.practice_areas.includes(a) ? f.practice_areas.filter((x) => x !== a) : [...f.practice_areas, a] }));
  useEffect(() => { api.get("/directory/practice-areas").then(({ data }) => setAreas(data.practice_areas)).catch(() => {}); }, []);

  const submitListing = async (e) => {
    e.preventDefault();
    if (!listing.name.trim() || !listing.email.trim()) { toast.error(L.nameEmailReq); return; }
    setListingBusy(true);
    const payload = { ...listing, counties: listing.county ? [listing.county] : [] };
    try { await api.post("/directory/attorneys/request", payload); setListingDone(true); }
    catch { toast.error(L.err); }
    setListingBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error(L.emailReq); return; }
    setBusy(true);
    try { await api.post("/attorneys/opt-in", form); setDone(true); }
    catch { toast.error(L.err); }
    setBusy(false);
  };

  return (
    <div>
      <Navbar />
      <Helmet>
        <title>{L.metaTitle}</title>
        <meta name="description" content={L.metaDesc} />
      </Helmet>
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="overline text-accent mb-4">{L.over}</p>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-5">
            {L.h1}
          </h1>
          <p className="text-muted-foreground leading-relaxed mb-8 max-w-lg">
            {L.intro} <b>{L.plans("$59", "$3.95")}</b>
          </p>
          <div className="space-y-5">
            {L.perks.map(([title, desc], i) => {
              const Icon = PERK_ICONS[i];
              return (
                <div key={title} className="flex gap-4">
                  <span className="grid place-items-center w-10 h-10 shrink-0 bg-primary text-primary-foreground"><Icon size={18} /></span>
                  <div><p className="font-semibold">{title}</p><p className="text-sm text-muted-foreground">{desc}</p></div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid-card p-8" data-testid="attorney-optin-card">
          {done ? (
            <div className="text-center py-8" data-testid="optin-success">
              <CheckCircle2 className="mx-auto text-emerald-600 mb-4" size={40} />
              <h3 className="font-serif text-2xl mb-2">{L.onList}</h3>
              <p className="text-sm text-muted-foreground mb-6">{L.onListBody}</p>
              <a href="/pricing"><Button className="rounded-sm bg-accent text-accent-foreground">{L.seePricing}</Button></a>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2"><Scale size={18} className="text-accent" /><p className="overline text-accent">{L.joinOver}</p></div>
              <h3 className="font-serif text-2xl mb-1">{L.joinH}</h3>
              <p className="text-sm text-muted-foreground mb-6">{L.joinSub}</p>
              <form onSubmit={submit} className="space-y-4">
                <div><Label className="text-xs">{L.fullName}</Label><Input data-testid="optin-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs">{L.workEmail}</Label><Input data-testid="optin-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs">{L.firm}</Label><Input data-testid="optin-firm" value={form.firm} onChange={(e) => set("firm", e.target.value)} className="rounded-sm mt-1" /></div>
                <Button type="submit" disabled={busy} data-testid="optin-submit" className="w-full rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                  <Check size={16} /> {busy ? L.submitting : L.join}
                </Button>
                <p className="text-[0.7rem] text-muted-foreground text-center">{L.joinLegal}</p>
              </form>
            </>
          )}
        </div>
      </section>

      {/* Request a directory listing */}
      <section className="bg-secondary/40 border-y-2 border-black/5" data-testid="listing-request-section">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="flex items-center gap-2 mb-3"><Gavel size={18} className="text-accent" /><p className="overline text-accent">{L.dirOver}</p></div>
          <h2 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight mb-3">{L.dirH}</h2>
          <p className="text-muted-foreground mb-8 max-w-xl">
            {L.dirSub}
          </p>

          {listingDone ? (
            <div className="grid-card p-8 text-center" data-testid="listing-success">
              <CheckCircle2 className="mx-auto text-emerald-600 mb-4" size={40} />
              <h3 className="font-serif text-2xl mb-2">{L.sentH}</h3>
              <p className="text-sm text-muted-foreground">{L.sentBody}</p>
            </div>
          ) : (
            <form onSubmit={submitListing} className="grid-card p-6 sm:p-8 space-y-4" data-testid="listing-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">{L.attyName}</Label><Input data-testid="listing-name" value={listing.name} onChange={(e) => setL("name", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs">{L.firm}</Label><Input data-testid="listing-firm" value={listing.firm} onChange={(e) => setL("firm", e.target.value)} className="rounded-sm mt-1" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">{L.workEmail}</Label><Input data-testid="listing-email" type="email" value={listing.email} onChange={(e) => setL("email", e.target.value)} className="rounded-sm mt-1" /></div>
                <div><Label className="text-xs">{L.phone}</Label><Input data-testid="listing-phone" value={listing.phone} onChange={(e) => setL("phone", e.target.value)} className="rounded-sm mt-1" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-xs">{L.city}</Label><Input value={listing.city} onChange={(e) => setL("city", e.target.value)} className="rounded-sm mt-1" /></div>
                <div>
                  <Label className="text-xs">{L.county}</Label>
                  <select value={listing.county} onChange={(e) => setL("county", e.target.value)} data-testid="listing-county"
                    className="w-full h-10 rounded-sm border-2 border-black/15 bg-background px-3 text-sm mt-1">
                    <option value="">{L.selectCounty}</option>
                    {NC_COUNTIES.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div><Label className="text-xs">{L.website}</Label><Input value={listing.website} onChange={(e) => setL("website", e.target.value)} className="rounded-sm mt-1" placeholder="https://" /></div>
              <div>
                <Label className="text-xs">{L.areas}</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="listing-practice-areas">
                  {areas.map((a) => (
                    <button type="button" key={a} onClick={() => toggleArea(a)}
                      className={`text-xs px-2.5 py-1 border-2 transition-colors ${listing.practice_areas.includes(a) ? "border-accent bg-accent/10" : "border-black/15 hover:border-black/40"}`}>
                      {areaLabel(lang, a)}
                    </button>
                  ))}
                </div>
              </div>
              <div><Label className="text-xs">{L.bio}</Label><Textarea value={listing.bio} onChange={(e) => setL("bio", e.target.value)} rows={3} className="rounded-sm mt-1" placeholder={L.bioPh} /></div>
              <Button type="submit" disabled={listingBusy} data-testid="listing-submit"
                className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                <Gavel size={16} /> {listingBusy ? L.submitting : L.submitListing}
              </Button>
              <p className="text-[0.7rem] text-muted-foreground">{L.listingLegal}</p>
            </form>
          )}
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-6 pb-20" data-testid="attorneys-featured-cta">
        <div className="grid-card p-8 flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-xl">
            <p className="overline text-accent mb-2">{L.featOver}</p>
            <h2 className="font-serif text-3xl tracking-tight mb-2">{L.featH}</h2>
            <p className="text-muted-foreground">{L.featBody}</p>
          </div>
          <Link to="/featured"><Button className="rounded-sm" data-testid="attorneys-get-featured-btn">{L.getFeatured}</Button></Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
